'use client'
import { PrismicRichText, PrismicLink } from "@prismicio/react";
import { isFilled } from "@prismicio/client";

/**
 * @typedef {import("@prismicio/client").Content.ArticlePageSlice} ArticlePageSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ArticlePageSlice>} ArticlePageProps
 * @type {import("react").FC<ArticlePageProps>}
 */
const ArticlePage = ({ slice }) => {
  const { article } = slice.primary;
  const imageUrl = article.data.preview_image?.url;
  const imageAlt = article.data.preview_image?.alt || article.data.preview_title?.[0]?.text || "Image de l'article";
  const date = isFilled.date(article.data.preview_date)
    ? new Date(article.data.preview_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'numeric', day: 'numeric' })
    : null;
  const tag = article.tags?.[0];


  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pt-16 pb-20 md:pt-23 md:pb-30 bg-light-gray framing text-dark-green"
    >
      <div className="max-w-375 mx-auto w-full">
        <article
          className={`bg-white w-full h-full flex flex-col md:flex-row min-h-90`}
          itemScope
          itemType="https://schema.org/BlogPosting"
        >
          <div
            className={`relative bg-cover bg-center bg-no-repeat min-h-50 md:w-[40%] lg:w-[45%] w-full`}
            style={{ backgroundImage: `url(${imageUrl})` }}
          >
            <img
              src={imageUrl}
              alt={imageAlt}
              className="absolute inset-0 w-full h-full object-cover opacity-0"
              itemProp="image"
            />
            <img src="/y-img.svg" alt="image de décoration" className="absolute z-[10] h-full w-auto object-contain top-0 -right-[2px]" />
          </div>
          <div className="md:w-[60%] lg:w-[55%] w-full p-8 md:pl-5 md:pr-15 flex flex-col my-auto">
            {date && (
              <p className="text-date mb-2" itemProp="datePublished">{date}</p>
            )}
            <div className="line-clamp-7 md:line-clamp-5 mb-10">
              {isFilled.richText(article.data.preview_title) && (
                <PrismicRichText
                  field={article.data.preview_title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="h3-sm normal-case mb-2.5" itemProp="headline">{children}</h2>
                    ),
                    heading3: ({ children }) => (
                      <h2 className="h3-sm normal-case mb-2.5" itemProp="headline">{children}</h2>
                    ),
                  }}
                />
              )}
              {isFilled.richText(article.data.preview_description) && (
                <PrismicRichText
                  field={article.data.preview_description}
                  components={{
                    paragraph: ({ children }) => (
                      <p itemProp="description">{children}</p>
                    ),
                  }}
                />
              )}
            </div>
            <div className="flex justify-between items-end">
              {tag && (
                <span className="text-tags tag-light-green text-white" itemProp="keywords">{tag}</span>
              )}
              <div className="flex-1 text-end">
                <PrismicLink
                  document={article}
                  className="p-sm inline-block py-1"
                  aria-label={`Lire plus sur ${article.data.preview_title?.[0]?.text || "l'article"}`}
                  itemProp="url"
                >
                  Lire plus
                </PrismicLink>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default ArticlePage;
